import { useQuery } from '../../../hooks/useQuery';
import { FormEvent, useEffect, useState } from 'react';
import { Link, Redirect } from 'react-router-dom';
import { Logo } from '../../components/Logo/Logo';
import { Button, Container, Form, InputGroup, Modal } from 'react-bootstrap';
import { auth } from '../../../firebase';
import { FullscreenLoader } from '../../components/FullscreenLoader/FullscreenLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { ErrorModal } from '../../components/ErrorModal/ErrorModal';
import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';
import { useTranslation } from 'react-i18next';

const AuthHandlerComponent = () => {
  const { mode, oobCode, continueUrl } = useQuery();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  const [isAskingNewPassword, setIsAskingNewPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  const [errorCode, setErrorCode] = useState<string>(null);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

  const handleSuccess = () => {
    setSuccess(true);
    setIsSuccessModalOpen(true);
  };

  const handleError = (error) => {
    setError(true);
    setErrorCode(error.code);
    setIsErrorModalOpen(true);
  };

  const onPasswordChangeSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsAskingNewPassword(false);
    setError(false);
    setLoading(true);
    auth
      .confirmPasswordReset(oobCode, newPassword)
      .then(handleSuccess)
      .catch((error) => {
        handleError(error);
        setIsAskingNewPassword(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    switch (mode) {
      case 'verifyEmail': {
        setError(false);
        setLoading(true);
        auth
          .applyActionCode(oobCode)
          .then(handleSuccess)
          .catch(handleError)
          .finally(() => setLoading(false));
        break;
      }
      case 'resetPassword': {
        setError(false);
        setLoading(true);
        auth
          .verifyPasswordResetCode(oobCode)
          .then(() => {
            setIsAskingNewPassword(true);
          })
          .catch(handleError)
          .finally(() => setLoading(false));
      }
    }
  }, [mode]);

  if (!mode || !oobCode) return <Redirect to={'/'} />;

  return (
    <>
      <Container className={'d-flex flex-column align-items-center my-5'}>
        <Logo className={'mb-5'} />
        {loading && <FullscreenLoader fullscreen={false} />}
        {success && <FontAwesomeIcon icon={faCheckCircle} size={'4x'} color={'green'} />}
        {error && <FontAwesomeIcon icon={faTimesCircle} size={'4x'} color={'red'} />}

        {isAskingNewPassword && (
          <Form onSubmit={onPasswordChangeSubmit}>
            <Form.Group>
              <Form.Label htmlFor={'password'}>{t('authHandler.labels.inserisciNuovaPassword')}</Form.Label>
              <InputGroup>
                <Form.Control
                  required
                  size={'lg'}
                  type={passwordVisible ? 'text' : 'password'}
                  id={'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <InputGroup.Append>
                  <Button variant={'secondary'} onClick={() => setPasswordVisible((previousState) => !previousState)}>
                    <FontAwesomeIcon icon={passwordVisible ? faEyeSlash : faEye} fixedWidth={true} />
                  </Button>
                </InputGroup.Append>
              </InputGroup>
            </Form.Group>
            <Button variant={'primary'} type={'submit'}>
              {t('labels.conferma')}
            </Button>
          </Form>
        )}
      </Container>

      <Modal show={isSuccessModalOpen} onHide={() => setIsSuccessModalOpen(false)} centered backdrop={'static'} keyboard={false}>
        <Modal.Header>
          <Modal.Title>{t('authHandler.labels.operazioneSuccesso')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t('authHandler.labels.operazioneDescrizione', { title: process.env.REACT_APP_TITLE })}</Modal.Body>
        <Modal.Footer>
          <Button variant={'primary'} as={Link} to={continueUrl ?? '/'} onClick={() => setIsSuccessModalOpen(false)}>
            {t('labels.chiudi')}
          </Button>
        </Modal.Footer>
      </Modal>

      <ErrorModal isModalOpen={isErrorModalOpen} setIsModalOpen={setIsErrorModalOpen} errorCode={errorCode} />
    </>
  );
};

export default AuthHandlerComponent;
